/**
 * Remote control of a live workbench session from the console and the phone (D282, docs/remote-control.md):
 * AIOE's own view types. The session's shape is the workbench's (./remoteControl.ts, mirrored byte for byte);
 * this file adds only what the organisation knows about where it runs.
 *
 *   GET /people/v1/me/live          the caller's own sessions open to remote control, from every device's
 *                                   latest heartbeat (`canControl` always true)
 *   GET /projects/v1/:id/live       the sessions in a project the caller may watch: their own, any Team Work,
 *                                   and Chat or Research shared with the project or with them by name
 *
 * Both answer {@link LiveSessionList}, newest `lastSeenAt` first. The live detail itself goes over the relay
 * (`POST /relay/v1/devices/:deviceId/requests`, `sessions.subscribe` and the control methods).
 */
import { z } from "zod";
import { remoteSessionSchema } from "./remoteControl.js";
/** One session open to remote control, with the machine it runs on. */
export const liveSessionRowSchema = z.object({
    deviceId: z.string(),
    /** The machine's name, as the fleet shows it. */
    deviceName: z.string(),
    /** The machine was heard from within the fleet's online window. */
    online: z.boolean(),
    /** When the machine was last heard from (ISO): "last seen 10 min ago". */
    lastSeenAt: z.string(),
    session: remoteSessionSchema,
    /** The project it belongs to, when the organisation can tell (by repository or by conversation). */
    projectId: z.string().optional(),
    projectName: z.string().optional(),
    /** The caller is the session's person: they may send, interrupt, approve and stop. Everyone else may only watch. */
    canControl: z.boolean(),
});
/** `GET /people/v1/me/live` and `GET /projects/v1/:id/live`. */
export const liveSessionListSchema = z.object({ sessions: z.array(liveSessionRowSchema) });
/**
 * The control methods only the session's person may call over the relay, each with `via` in its params. D372 adds
 * `board.setPaused` (the task strip's Pause all / Resume all), which the workbench gates on a session open to remote
 * control working on that board; AIOE 0.76.0 adds `sessions.handOff` (the usage limit's "Hand off to…", D357:
 * `{ sessionId, target: { kind: "runtime", provider, model? } | { kind: "account", account } }`; an account must be the
 * same runtime's, D414). D414 adds `ui.press` (a press on a block an agent showed:
 * `{ conversationId, messageId, blockId, action, values?, via }` → `{ ok: true }`; a refusal is -32003 with
 * `data.error: "ui_press_refused"` and the workbench's sentence).
 */
export const REMOTE_CONTROL_METHODS = ["sessions.send", "sessions.interrupt", "sessions.stop", "permissions.resolve", "sessions.remote", "board.setPaused", "sessions.handOff", "ui.press"];
//# sourceMappingURL=remoteLive.js.map